<template>
  <div data-layout-v="3.3.1" class="page">
    <AppHeader :key="route.fullPath" :page-not-found="false" />
    <SlimBanner
      v-if="renderedGlobalSiteNotification"
      :data="renderedGlobalSiteNotification"
      size="small"
    />
    <slot />
    <AppFooter />

    <!-- <TheCookiePrompt
      v-if="!globalStore.use3rdPartyCookieManagement && !pageNotFound"
    /> -->

    <LocaleSetter v-if="localeModalStore.showLocaleModal" />
    <div id="teleports" />

    <ClientOnly>
      <SiteNotificationManager
        :global-entries="globalNotificationEntries"
        :page-entries="pageNotificationEntries"
        :account-id="accountId"
        :logged-in="loggedIn"
        :roles-retrieved="rolesRetrieved"
        :roles="roles"
        :stores="stores"
        :visitor-language="visitorLanguageCookie"
        :visitor-country="visitorCountryCookie"
        :route-slug="route.params.slug"
        :references-ready="siteNotificationReferencesReady"
        :condition-mock="siteNotificationConditionMock"
        :locale-modal-open="localeModalStore.showLocaleModal"
      />
    </ClientOnly>

    <div
      v-if="globalStore.isInspectorMode && globalStore.pageEntrySys?.id"
      class="global-page-inspector"
    >
      <div class="page-inspector-badge">
        <span class="badge-type">
          Page ID:
          <span class="badge-base-type">{{
            globalStore.pageEntrySys?.contentType?.sys?.id || 'Page'
          }}</span>
        </span>
        <a
          :href="`https://app.contentful.com/spaces/0piqveu8x9oj/entries/${globalStore.pageEntrySys.id}`"
          target="_blank"
          class="badge-link"
          title="Open Contentful Page Entry"
        >
          {{ globalStore.pageEntrySys.id }}
          <IconExternalLink width="12" height="12" />
        </a>
        <EntryStatus
          v-if="globalStore.pageEntrySys"
          :status="getEntryStatus(globalStore.pageEntrySys)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { getEntryStatus } from '~/utils/contentfulStatus'
import type {
  TypeContentBlockWithoutUnresolvableLinksResponse,
  TypeGlobalSettingsSkeleton,
} from '~/types/contentful'
import { shouldRenderGlobalSiteNotification } from '~/utils/globalSiteNotification'
import { getSiteNotificationReferences } from '~/utils/siteNotification'
import { getSiteNotificationConditionMock } from '~/utils/siteNotificationConditionMock'

// Extend the Window interface to include WebComponentsRegistry

declare global {
  interface Window {
    WebComponentsRegistry?: {
      setContentfulConfig: (config: {
        spaceId: string
        accessToken: string
      }) => void
      register: (elements: string[]) => void
      registerAll?: () => void
    }
  }
}

const globalStore = useGlobalStore()
const localeModalStore = useLocalModalStore()
const siteMeasurementStore = useSiteMeasurementStore()
const userStore = useUserStore()
const { accountId, loggedIn, rolesRetrieved, roles, stores } =
  storeToRefs(userStore)
const route = useRoute()
const { locale } = useI18n()
const runtimeConfig = useRuntimeConfig()
const siteNotificationPreviewMode = computed(
  () => runtimeConfig.public.ctfHost === 'preview.contentful.com',
)
const siteNotificationConditionMock = computed(() =>
  siteNotificationPreviewMode.value
    ? getSiteNotificationConditionMock(route.query)
    : null,
)
const visitorLanguageCookie = useCookie<string | undefined>('visitor_language')
const visitorCountryCookie = useCookie<string | undefined>('visitor_country')
const siteNotificationStore = useSiteNotificationStore()
const pageNotificationEntries = computed(() =>
  siteNotificationStore.pageCandidatesForRoute(route.path),
)
const siteNotificationReferencesReady = computed(
  () =>
    siteNotificationStore.pageRoutePath === null ||
    siteNotificationStore.pageRoutePath === route.path,
)

const { data: globalSettings } = await useAsyncData(
  `global-settings-${locale.value}`,
  async () => {
    const { $client } = useNuxtApp()
    try {
      return await $client.withoutUnresolvableLinks.getEntry<TypeGlobalSettingsSkeleton>(
        '5US83uannV96QPXZ2neuuN',
        { include: 10, locale: locale.value },
      )
    } catch (error) {
      console.error('Failed to fetch global settings:', error)
      return null
    }
  },
)

// Computed
const pageNotFound = computed(() => {
  const error = useError()
  return error.value?.statusCode === 404
})

const globalSiteNotification = computed(() => {
  const notification = globalSettings.value?.fields?.globalSiteNotification as
    | TypeContentBlockWithoutUnresolvableLinksResponse
    | undefined
  return notification?.fields?.renderAs === 'SlimBanner' ? notification : null
})

const globalNotificationEntries = computed(() =>
  getSiteNotificationReferences(globalSettings.value),
)

const { visibilityStatus: globalSiteNotificationVisibilityStatus } =
  usePageVisibility({
    visibility: () => globalSiteNotification.value?.fields?.visibility,
    onDenied: () => undefined,
  })

const renderedGlobalSiteNotification = computed(() => {
  const notification = globalSiteNotification.value

  return shouldRenderGlobalSiteNotification(
    notification,
    globalSiteNotificationVisibilityStatus.value,
  )
    ? notification
    : null
})

// Methods
const getViewportSize = () =>
  siteMeasurementStore?.setViewportSize?.({
    width: window?.innerWidth,
    height: window?.innerHeight,
  })

const addWindowResizeListener = () =>
  window?.addEventListener?.('resize', () => getViewportSize())

const checkVisitorCookies = () => {
  const languageCookie = useCookie('visitor_language')
  const countryCookie = useCookie('visitor_country')
  if (!pageNotFound.value && (!languageCookie.value || !countryCookie.value)) {
    localeModalStore.showLocaleModal = true // prompt locale modal
  }

  const cookieStore = useCookieStore()
  if (languageCookie.value)
    cookieStore.languageCookie = validateLocale(languageCookie.value)
  if (countryCookie.value) cookieStore.countryCookie = countryCookie.value
}

const handleketchShowPreferences = (e: MouseEvent) => {
  e.preventDefault()
  try {
    if (typeof window.ketch === 'function') {
      window.ketch('showPreferences')
    } else {
      console.warn('ketch is not defined or not a function')
    }
  } catch (error) {
    console.error("error when calling ketch('showPreferences')")
    console.error({ error })
  }
}

const attachKetchPreferencesListener = () => {
  const ketchShowPreferencesButton = document.getElementById(
    'ketch-show-preferences',
  )
  if (ketchShowPreferencesButton) {
    ketchShowPreferencesButton.addEventListener('click', (e) =>
      handleketchShowPreferences(e),
    )
  }
}

const waitForElementToResolve = (hash: string) =>
  new Promise<Element | null>((resolve, reject) => {
    if (!hash) reject(null)

    // Check if the element already exists
    const el = document.querySelector(hash)
    if (el) resolve(el)

    // Set up a MutationObserver to watch for changes in the DOM
    const observer = new MutationObserver((mutations, obs) => {
      // Check if the element exists after each DOM change
      const targetEl = document.querySelector(hash)
      if (targetEl) {
        // Resolve the promise with the element
        resolve(targetEl)
        // Stop observing
        obs.disconnect()
      }
    })

    // Start observing the document body for changes
    observer.observe(document.body, {
      childList: true, // Observe direct children
      subtree: true, // Observe all descendants
    })
  })

const detectHashVal = () => {
  const { hash } = useRoute()
  if (hash) {
    waitForElementToResolve(hash)?.then((el) => {
      if (el) el.scrollIntoView(true)
    })
  }
}

const loadCustomElementScript = () => {
  const hash = 'D2Oz9ym_'
  const fileName = `web-components-registry.${hash}`

  const scriptExists = document.querySelector(
    `script[src="/js/${fileName}.js"]`,
  )

  if (!scriptExists) {
    const script = document.createElement('script')
    script.src = `/js/${fileName}.js`
    script.type = 'text/javascript' // UMD, not module!
    script.onload = () => {
      if (window.WebComponentsRegistry) {
        const {
          setContentfulConfig,
          register,
          // registerAll, // this will register all elements in the registry
        } = window.WebComponentsRegistry

        setContentfulConfig({
          spaceId: 's5n2t79q9icq', // magic.wizards
          accessToken: 'poEmXFXQhqamkmkKDV0cDt-SeuT2He6QWwlk7mGCOtM', // production only
        })

        register([
          'auto-card',
          'magic-card',
          'magic-cards',
          'mana-icon',
          // 'unsupported-element', // this will trigger a warning in console
        ])
      } else {
        console.warn('WebComponentsRegistry is not available on window')
      }
    }
    document.body.appendChild(script)
  }

  return this
}

const handleInspectorToggle = (e: KeyboardEvent) => {
  const config = useRuntimeConfig()
  // Disable inspector mode in production (when connected to Contentful CDN)
  if (config.public.ctfHost === 'cdn.contentful.com') return

  if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'b') {
    e.preventDefault()
    globalStore.isInspectorMode = !globalStore.isInspectorMode
  }
}

const onWindowLoaded = () => {
  globalStore.isWindowLoaded = true

  if (typeof window !== 'undefined') {
    getViewportSize()
    addWindowResizeListener()
    checkVisitorCookies()
    attachKetchPreferencesListener()
    detectHashVal()
    loadCustomElementScript()
  }
}

onMounted(() => {
  onNuxtReady(() => onWindowLoaded())
  window.addEventListener('keydown', handleInspectorToggle)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', getViewportSize)
  window.removeEventListener('keydown', handleInspectorToggle)
})
</script>

<style lang="scss">
:root {
  --black: #000;
  --black-alpha10: rgba(0, 0, 0, 0.1);
  --black-alpha50: #{rgba(#000, 0.5)};

  --white: #fff;
  --white-alpha25: #{rgba(#fff, 0.25)};

  --border-radius: 5px;

  /* brands colors */
  --purple: #504e8b;
  --purple-light: #7a79c1;
  --purple-dark: #47457b;
  --orange: #e73511;
  --orange-light: #ee7158;

  --gray-1: #f5f5f5;
  --gray-2: #ebebeb;
  --gray-3: #e6e6e6;
  --gray-4: #e3e3e3;
  --gray-5: #cbcbcb;
  --gray-6: #c7c7c7;
  --gray-7: #737373;
  --gray-8: #383838;

  --green: #0b4a1d;

  /* html */
  --html-background: var(--gray-1);

  /* fonts */
  --base-font-family: 'Open Sans', Arial, sans-serif;
  --base-font-size: 16px;

  --roboto-condensed: 'Roboto Condensed', Arial, serif;
  --georgia: 'Georgia', Arial, serif;

  /* z indexes */
  --modal-z-index: 1000;

  --site-header-content-max-width: 1600px;
  --site-header-content-padding-x: 15px;

  --swiper-overflow: visible;

  --display-sm-and-up: none;
  --display-md-and-up: none;
  --display-lg-and-up: none;

  @include media('sm') {
    --hidden-sm-and-up: none;
    --display-sm-and-up: block;
  }

  @include media('md') {
    --hidden-md-and-up: none;
    --display-md-and-up: block;
  }

  @include media('lg') {
    --hidden-lg-and-up: none;
    --display-lg-and-up: block;
  }

  --container-max-width: 1350px;
  --container-padding: 0 20px;
}

html {
  background: var(--html-background);
  box-sizing: border-box;
  color: var(--black);
  font-family: var(--base-font-family);
  font-size: var(--base-font-size);
  -ms-text-size-adjust: 100%;
  -webkit-text-size-adjust: 100%;
  -moz-osx-font-smoothing: grayscale;
  -webkit-font-smoothing: antialiased;
}

*,
*:before,
*:after {
  box-sizing: border-box;
  margin: 0;
}

.page {
  margin: auto;
  max-width: 1920px;
}

[role='button'],
[type='button'],
[type='reset'],
[type='submit'],
button {
  cursor: pointer;
}

a {
  color: var(--purple-light);
  text-decoration: none;
}

// Hides browser default 'x' in search inputs
input[type='search']::-webkit-search-decoration,
input[type='search']::-webkit-search-cancel-button,
input[type='search']::-webkit-search-results-button,
input[type='search']::-webkit-search-results-decoration {
  display: none;
}

.swiper-container {
  overflow: var(--swiper-overflow) !important;
}

.ta-center {
  text-align: center;
}

.v-popper--theme-tooltip {
  color: var(--white);
  font-size: 16px;

  .v-popper__inner {
    max-width: 600px;
    background: var(--purple);
  }

  .v-popper__arrow-outer,
  .v-popper__arrow-inner {
    border-color: var(--purple);
  }
}

.global-page-inspector {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 9999;
  pointer-events: none;
}

.page-inspector-badge {
  pointer-events: auto;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border: 2px solid var(--purple);
  border-radius: 8px;
  padding: 8px 14px;
  display: flex;
  align-items: center;
  gap: 16px;
  font-family: var(--base-font-family);
  font-size: 14px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
}

.page-inspector-badge:hover {
  background: rgba(255, 255, 255, 1);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
}

.global-page-inspector .badge-type {
  font-weight: 700;
  color: var(--purple-dark);
}

.global-page-inspector .badge-base-type {
  font-weight: 500;
  color: var(--gray-7);
  font-size: 12px;
  text-transform: capitalize;
}

.global-page-inspector .badge-link {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--purple);
  text-decoration: none;
  font-family: monospace;
  font-size: 13px;
  background: rgba(80, 78, 139, 0.1);
  padding: 4px 8px;
  border-radius: 4px;
  transition: background 0.2s ease;
}

.global-page-inspector .badge-link:hover {
  background: rgba(80, 78, 139, 0.2);
  color: var(--purple-dark);
}
</style>
